import { OptionallyVisible } from 'components';
import { Fragment, MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { Shift4Logo } from 'components/shift4Logo';

import { OrderPwcData } from 'pages/pwcOrders/view/types';

import { OrderItem, OrderItemType } from 'types/pwcOrder';

import { formatCryptoAmountWithSymbolAtEnd } from 'utils/cryptoFormatter';
import { formatDateLocal } from 'utils/date';
import { capitalizeFirstLetter } from 'utils/string';

import { KEYS, LABELS } from './keys';
import {
  Container,
  Delimiter,
  Item,
  ItemName,
  ItemValue,
  ItemValueInactive,
  ItemVertical,
  Logo,
  OrderSection,
  Title,
  TotalOrderSection,
  Wrapper,
} from './styles';
import { OrderListType, getListOfItems } from './utils';

interface ReceiptProps {
  targetRef: MutableRefObject<any>;
  orderData: OrderPwcData;
}

const CustomerInfo = ({ firstName, lastName, email }: { firstName: string; lastName: string; email: string }) => {
  const { t } = useTranslation();
  return (
    <Wrapper>
      <Item>
        <ItemName>{t(LABELS.CUSTOMER_NAME)}</ItemName>
        <ItemValue>{`${firstName} ${lastName}`}</ItemValue>
      </Item>
      <Item>
        <ItemName>{t(LABELS.CUSTOMER_EMAIL)}</ItemName>
        <ItemValue>{email}</ItemValue>
      </Item>
    </Wrapper>
  );
};

const ChargesList = ({ charges }: { charges: OrderPwcData['charges'] }) => {
  const { t } = useTranslation();
  return (
    <>
      {charges.map((charge) => (
        <Fragment key={charge.uuid}>
          <Item>
            <ItemName>{t(LABELS.AMOUNT_PAID_IN_CRYPTO)}</ItemName>
            <ItemValue>
              {formatCryptoAmountWithSymbolAtEnd(charge.cryptoPayment.cryptoAmount, charge.cryptoPayment.cryptoCurrencyCode)}
            </ItemValue>
          </Item>
          <Item>
            <ItemName>{t(LABELS.NETWORK)}</ItemName>
            <ItemValue>{capitalizeFirstLetter(charge.cryptoPayment.network ?? '')}</ItemValue>
          </Item>
        </Fragment>
      ))}
    </>
  );
};

const WalletInfo = ({ charges }: { charges: OrderPwcData['charges'] }) => {
  const { t } = useTranslation();
  return (
    <Wrapper>
      {charges.map((charge) => (
        <Fragment key={charge.uuid}>
          <ItemVertical>
            <ItemName>{t(LABELS.SENDING_WALLET)}</ItemName>
            <ItemValueInactive>{charge.cryptoExchange?.toWalletAddress}</ItemValueInactive>
          </ItemVertical>
          <ItemVertical>
            <ItemName>{t(LABELS.TRANSACTION_HASH)}</ItemName>
            <ItemValueInactive>{charge.cryptoPayment.onChain?.transactionHash}</ItemValueInactive>
          </ItemVertical>
        </Fragment>
      ))}
    </Wrapper>
  );
};

export const Receipt = ({ targetRef, orderData }: ReceiptProps) => {
  const { t } = useTranslation();
  const merchantName = orderData.merchant.name;
  const firstName = orderData.customer?.firstName ?? KEYS.DATA_PLACEHOLDER;
  const lastName = orderData.customer?.lastName ?? KEYS.DATA_PLACEHOLDER;
  const email = orderData.customer?.email ?? KEYS.DATA_PLACEHOLDER;

  const totalItem: OrderItem = {
    uuid: 'total-item',
    name: t(LABELS.TOTAL),
    imageUrl: null,
    fiatAmount: Number(orderData.general.fiatAmount),
    orderUuid: '',
    type: OrderItemType.Custom,
  };

  const listConfigs = [
    { id: 'items', type: OrderListType.Items, items: orderData.general.items, isTotal: false },
    { id: 'fees', type: OrderListType.Fees, items: orderData.general.items, isTotal: false },
    { id: 'total', type: OrderListType.Fees, items: [totalItem], isTotal: true },
  ];

  return (
    <Container ref={targetRef}>
      <Title>{t(LABELS.PAGE_TITLE, { merchantName })}</Title>

      <Wrapper>
        <Item>
          <ItemName>{t(LABELS.ORDER_ID)}</ItemName>
          <ItemValueInactive>{orderData.general.uuid}</ItemValueInactive>
        </Item>
      </Wrapper>

      <CustomerInfo firstName={firstName} lastName={lastName} email={email} />

      <Wrapper>
        <Item>
          <ItemName>{t(LABELS.ORDER_DATE)}</ItemName>
          <ItemValue>{formatDateLocal(orderData.general.createdAt)}</ItemValue>
        </Item>
        <Item>
          <ItemName>{t(LABELS.PAYMENT_METHOD)}</ItemName>
          <ItemValue>{KEYS.PAYMENT_METHOD}</ItemValue>
        </Item>
        <Item>
          <ItemName>{t(LABELS.AMOUNT_PAID_IN_FIAT)}</ItemName>
          <ItemValue>{`${orderData.general.paidFiatAmount} ${orderData.general.fiatCurrencyCode}`}</ItemValue>
        </Item>
        <ChargesList charges={orderData.charges} />
      </Wrapper>

      <Delimiter />

      <Wrapper>
        <OrderSection>
          <Item>
            <ItemName>{t(LABELS.ORDER_SUMMARY)}</ItemName>
          </Item>
        </OrderSection>

        {listConfigs.map(({ id, type, items, isTotal }, idx) => {
          const ListComponent = isTotal ? TotalOrderSection : OrderSection;

          return (
            <Fragment key={id}>
              <ListComponent>
                {getListOfItems({
                  items,
                  fiatCurrencyCode: orderData.general.fiatCurrencyCode,
                  listType: type,
                })}
              </ListComponent>

              <OptionallyVisible visible={idx !== listConfigs.length - 1}>
                <Delimiter />
              </OptionallyVisible>
            </Fragment>
          );
        })}
      </Wrapper>

      <Delimiter />

      <WalletInfo charges={orderData.charges} />

      <Wrapper>
        <Logo>
          {t(LABELS.POWERED_BY)} <Shift4Logo />
        </Logo>
      </Wrapper>
    </Container>
  );
};
